import classNames from "classnames";
import Modal from "neon-rc/lib/components/Modal";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import RedirectRenderer from "@utils/RedirectRenderer";
import ApiFactory from "neon-frontend-utils/src/api/factory";
import Logger from "neon-frontend-utils/src/logger";
import RedirectHelper from "neon-frontend-utils/src/redirectHelper";
import translator from "neon-frontend-utils/src/translator";
import OrganisationLogo from "../OrganisationLogo/OrganisationLogo";
import style from "./style/switchorganisationmodal.scss";

const SwitchOrganisationModal = ({ user, closeModal, isOpen, mode: selectOrganisationMode, languageId }) => {
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [fetchingOrganisations, setFetchingOrganisations] = useState(false);
    const [organisations, setOrganisations] = useState([]);
    const [selectedOrganisationTenantSlug, setSelectedOrganisationTenantSlug] = useState(null);

    const api = ApiFactory.get("neon");

    /**
     * Removes deactivated organizations from the response object and updates parent references
     * @param {Object} response - The original organizations response object
     * @returns {Object} - A new object with deactivated organizations removed
     */
    const removeDeactivatedOrganisations = (response) => {
        // Create a copy of the response to avoid mutating the original
        const filteredResponse = { ...response };
        const deactivatedOrganisations = [];

        // First pass: identify all deactivated organizations by their ids
        Object.keys(response).forEach((id) => {
            if (response[id].deactivatedOn !== null) {
                deactivatedOrganisations.push(id);
                // Mark for removal
                delete filteredResponse[id];
            }
        });

        // Second pass: remove deactivated organisations from children arrays
        Object.keys(filteredResponse).forEach((id) => {
            const node = filteredResponse[id];

            if (node.children && node.children.length > 0) {
                // Filter out deactivated children using their full keys
                node.children = node.children.filter((child) => !deactivatedOrganisations.includes(child));
            }
        });

        return filteredResponse;
    };

    const parseOrganisationResponse = (response) => {
        const orgs = {};

        Object.keys(response).forEach((newNode) => {
            const node = response[newNode];
            const pathNodes = node.path.split("/");
            const uuid = pathNodes[pathNodes.length - 1];

            orgs[newNode] = {
                children: node.children || [],
                id: newNode,
                indent: pathNodes.length - 1,
                isRoot: node.isRoot,
                logo: node.logo,
                name: node.name,
                organisationTenantSlug: node.organisationTenantSlug,
                path: node.path,
                type: node.type,
                uuid,
            };
        });

        return orgs;
    };

    const fetchOrganisations = () => {
        if (fetchingOrganisations) {
            return;
        }

        setFetchingOrganisations(true);

        api.get(api.getBaseUrl(), api.getEndpoints().organisationTenants, {
            urlParams: {
                parameters: {
                    depth: 20,
                },
            },
        })
            .then((response) => {
                if (response.errors && response.errors.length) {
                    throw new Error("Something went wrong while fetching organisations");
                }
                const filteredResponse = removeDeactivatedOrganisations(response);
                const parsedOrganisations = parseOrganisationResponse(filteredResponse);

                setOrganisations(parsedOrganisations);
            })
            .catch((error) => {
                Logger.instance.error({
                    component: "OrganisationSelect",
                    message: `Could not fetch organisations: ${error.message}`,
                });
            });
    };

    const getRootNodes = () => {
        const rootNodes = [];

        if (organisations) {
            for (const keyPath in organisations) {
                if (organisations[keyPath] && organisations[keyPath].isRoot) {
                    rootNodes.push(organisations[keyPath]);
                }
            }
        }
        return rootNodes;
    };

    const getChildNodes = (node) => {
        const nodes = organisations;

        return node.children ? node.children.map((path) => nodes[path]) : [];
    };

    const onSelectOrganisation = (organisationTenantSlug) => {
        setSelectedOrganisationTenantSlug(organisationTenantSlug);
    };

    const getNodeComponents = (nodes) => {
        const nodeComponents = [];

        nodes.forEach((node) => {
            const selected = selectedOrganisationTenantSlug === node.organisationTenantSlug;

            nodeComponents.push(
                <ListItem
                    key={node.uuid}
                    button
                    className={classNames(style.listItem, { [style.listItem__selected]: selected })}
                    selected={selected}
                    style={{
                        height: "40px",
                        paddingLeft: `${node.indent * 30 + 10}px`,
                    }}
                    onClick={() => onSelectOrganisation(node.organisationTenantSlug)}
                >
                    {node.logo ? (
                        <OrganisationLogo
                            classes={{
                                root: style.logo,
                            }}
                            height={30}
                            logoUrl={node.logo}
                            width={30}
                        />
                    ) : null}
                    {node.name}
                </ListItem>,
            );

            if (node.children && node.children.length) {
                const children = getNodeComponents(getChildNodes(node));

                children.forEach((child) => {
                    nodeComponents.push(child);
                });
            }
        });

        return nodeComponents;
    };
    const postSelectedOrganisation = () => {
        setButtonDisabled(true);
        if (selectedOrganisationTenantSlug) {
            api.getAuthenticator()
                .refreshAndGetUser(selectedOrganisationTenantSlug)
                .then((response) => {
                    if (!response || !response.getActiveOrganisationTenant()) {
                        throw new Error("Organisation selection failed");
                    }
                    RedirectRenderer.render(RedirectHelper.instance.getRedirectPath() || "/", true, null);
                })
                .catch((error) => {
                    Logger.instance.error({
                        component: "OrganisationSelect",
                        message: `Could not select organisation: ${error.message}`,
                    });
                });
        }
    };

    useEffect(() => {
        if (user && user.isTenantUser && !fetchingOrganisations) {
            fetchOrganisations();
        }
    }, [user, fetchingOrganisations]);

    const i18n = translator(languageId, ["login", "form"]);
    const rootNodes = getRootNodes();

    return (
        <Modal
            cancelText={i18n.form_cancel}
            classes={{
                footer: selectOrganisationMode ? style.modalFooter : "",
                header: selectOrganisationMode ? style.modalHeader : "",
                overlay: `${style.modalOverlay} ${selectOrganisationMode ? "modal__fullscreen" : ""}`,
            }}
            handleOkClick={() => {
                if (selectedOrganisationTenantSlug) {
                    postSelectedOrganisation();
                }
            }}
            isActionModal={true}
            isOpen={isOpen}
            lockConfirm={!selectedOrganisationTenantSlug || buttonDisabled}
            okText={i18n.login_select_organisation_submit}
            title={selectOrganisationMode ? i18n.login_select_organisation_title : i18n.login_switch_organisation_title}
            onClose={closeModal}
        >
            <div className={style.listWrapper}>
                <p>{i18n.login_select_organisation_subtitle}</p>
                <List classes={{ root: style.list }} component="nav">
                    {getNodeComponents(rootNodes)}
                </List>
            </div>
        </Modal>
    );
};

const mapStateToProps = (state) => ({
    languageId: state.switchLanguageReducer.languageId,
});

export default connect(mapStateToProps)(SwitchOrganisationModal);
